import { useState, useEffect } from "react";
import {
  FaArrowLeft,
  FaPhone,
  FaEnvelope,
  FaMoneyBillWave,
  FaBriefcase,
  FaClock,
  FaArrowTrendUp,
  FaArrowTrendDown,
  FaRocket,
  FaBullseye,
  FaTrophy,
  FaWifi,
} from "react-icons/fa6";
import { FaArrowTrendDown as FaArrowTrendDownFa, FaArrowTrendUp as FaArrowTrendUpFa, FaCalendarAlt, FaCheckCircle, FaMapMarkerAlt, FaUserCircle } from "react-icons/fa";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../../components/Header";
import { BaseUrl } from "../../../../../utilities/BaseUrl";

/* ============================================
   Dummy Data Marketing
   ============================================ */
const DUMMY_MARKETING = {
  id: "mkt-001",
  name: "Rizki Pratama",
  email: "rizki@danelanet.com",
  phone_number: "0813-9876-5432",
  role: { name: "Marketing" },
  status: { name: "Aktif" },
  image: null,
  address: "Jl. Raya Cilegon No. 45, Kota Cilegon, Banten",
  join_date: "2023-03-10",
  // Penghasilan
  earnings: {
    bulan_ini: 4350000,
    bulan_lalu: 3800000,
    total_tahun_ini: 38200000,
    trend: "up",
    rincian: [
      { id: "e-1", label: "Komisi PSB", amount: 3200000, desc: "16 PSB × Rp 200.000" },
      { id: "e-2", label: "Bonus Target", amount: 750000, desc: "Target 15 PSB tercapai" },
      { id: "e-3", label: "Bonus Loyalitas", amount: 400000, desc: "Bertugas > 1 tahun" },
    ],
    potongan: [
      { id: "p-1", label: "BPJS Kesehatan", amount: 150000 },
    ],
  },
  // PSB
  psb: {
    total: 187,
    bulan_ini: 16,
    bulan_lalu: 12,
    target_bulan_ini: 15,
    komisi_per_psb: 200000,
    ranking: 2,
    total_marketing: 8,
    recent: [
      { id: "psb-1", pelanggan: "Budi Santoso", internet_number: "ISP-00156", package: "10 Mbps", status: "Selesai", date: "2025-07-10", komisi: 200000 },
      { id: "psb-2", pelanggan: "Siti Aminah", internet_number: "ISP-00155", package: "20 Mbps", status: "Selesai", date: "2025-07-09", komisi: 200000 },
      { id: "psb-3", pelanggan: "Dewi Lestari", internet_number: "ISP-00153", package: "30 Mbps", status: "Selesai", date: "2025-07-08", komisi: 200000 },
      { id: "psb-4", pelanggan: "Rendi Kurniawan", internet_number: "ISP-00151", package: "10 Mbps", status: "Proses", date: "2025-07-07", komisi: 0 },
      { id: "psb-5", pelanggan: "Nina Safitri", internet_number: "ISP-00150", package: "20 Mbps", status: "Selesai", date: "2025-07-05", komisi: 200000 },
      { id: "psb-6", pelanggan: "Yoga Aditya", internet_number: "ISP-00149", package: "10 Mbps", status: "Gagal", date: "2025-07-04", komisi: 0 },
      { id: "psb-7", pelanggan: "Lina Marlina", internet_number: "ISP-00148", package: "10 Mbps", status: "Selesai", date: "2025-07-03", komisi: 200000 },
      { id: "psb-8", pelanggan: "Hendra Wijaya", internet_number: "ISP-00147", package: "20 Mbps", status: "Selesai", date: "2025-07-02", komisi: 200000 },
    ],
  },
};

/* ============================================
   Sub-Komponen: SectionCard
   ============================================ */
const SectionCard = ({ title, icon: Icon, children, className = "" }) => (
  <article
    className={`rounded-2xl p-5 sm:p-6 transition-all duration-300 ${className}`}
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
      backdropFilter: "blur(12px)",
    }}
  >
    <div className="flex items-center gap-3 mb-5">
      {Icon && (
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{
            background: "rgba(0, 180, 216, 0.1)",
            border: "1px solid rgba(0, 180, 216, 0.2)",
          }}
        >
          <Icon className="text-ocean-400 text-sm" />
        </div>
      )}
      <h3 className="font-display text-base sm:text-lg font-bold text-gray-100">
        {title}
      </h3>
    </div>
    {children}
  </article>
);

/* ============================================
   Sub-Komponen: StatusBadge
   ============================================ */
const StatusBadge = ({ status, size = "sm" }) => {
  const config = {
    "Aktif": "bg-emerald-500/12 text-emerald-400 border-emerald-500/20",
    "Selesai": "bg-emerald-500/12 text-emerald-400 border-emerald-500/20",
    "Proses": "bg-amber-500/12 text-amber-400 border-amber-500/20",
    "Dalam Pengerjaan": "bg-amber-500/12 text-amber-400 border-amber-500/20",
    "Gagal": "bg-red-500/12 text-red-400 border-red-500/20",
    "Nonaktif": "bg-slate-500/12 text-slate-400 border-slate-500/20",
  };

  const dotColor = {
    "Aktif": "bg-emerald-400",
    "Selesai": "bg-emerald-400",
    "Proses": "bg-amber-400",
    "Dalam Pengerjaan": "bg-amber-400",
    "Gagal": "bg-red-400",
    "Nonaktif": "bg-slate-400",
  };

  const styleClass = config[status] || "bg-slate-500/12 text-slate-400 border-slate-500/20";
  const dot = dotColor[status] || "bg-slate-400";
  const sizeClass = size === "xs" ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs";

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold tracking-wide border whitespace-nowrap ${styleClass} ${sizeClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {status}
    </span>
  );
};

/* ============================================
   Sub-Komponen: StatCard
   ============================================ */
const StatCard = ({ label, value, icon: Icon, color, trend, trendValue, sub }) => {
  const colorMap = {
    cyan: {
      bg: "rgba(0, 180, 216, 0.06)", border: "rgba(0, 180, 216, 0.2)",
      text: "text-cyan-400", iconBg: "rgba(0, 180, 216, 0.12)",
    },
    emerald: {
      bg: "rgba(16, 185, 129, 0.06)", border: "rgba(16, 185, 129, 0.2)",
      text: "text-emerald-400", iconBg: "rgba(16, 185, 129, 0.12)",
    },
    amber: {
      bg: "rgba(245, 158, 11, 0.06)", border: "rgba(245, 158, 11, 0.2)",
      text: "text-amber-400", iconBg: "rgba(245, 158, 11, 0.12)",
    },
    violet: {
      bg: "rgba(139, 92, 246, 0.06)", border: "rgba(139, 92, 246, 0.2)",
      text: "text-violet-400", iconBg: "rgba(139, 92, 246, 0.12)",
    },
    rose: {
      bg: "rgba(244, 63, 94, 0.06)", border: "rgba(244, 63, 94, 0.2)",
      text: "text-rose-400", iconBg: "rgba(244, 63, 94, 0.12)",
    },
  };

  const c = colorMap[color] || colorMap.cyan;

  return (
    <div className="p-4 rounded-xl" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        {Icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.iconBg}`}>
            <Icon className={`${c.text} text-sm`} />
          </div>
        )}
      </div>
      <p className={`text-2xl font-bold ${c.text} tracking-tight`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
      {trend && (
        <div className="flex items-center gap-1.5 mt-1.5">
          {trend === "up" ? (
            <FaArrowTrendUp className="text-emerald-400 text-xs" />
          ) : (
            <FaArrowTrendDown className="text-red-400 text-xs" />
          )}
          <span className={`text-[11px] font-semibold ${trend === "up" ? "text-emerald-400" : "text-red-400"}`}>
            {trendValue}
          </span>
          <span className="text-[11px] text-slate-500">vs bulan lalu</span>
        </div>
      )}
    </div>
  );
};

/* ============================================
   Sub-Komponen: ProgressBar
   ============================================ */
const ProgressBar = ({ value, max, color = "cyan" }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const colorMap = {
    cyan: "bg-cyan-400",
    emerald: "bg-emerald-400",
    amber: "bg-amber-400",
    red: "bg-red-400",
  };
  const trackColor = "bg-slate-700/50";
  const barColor = colorMap[color] || colorMap.cyan;

  return (
    <div className="w-full">
      <div className={`h-2 rounded-full ${trackColor} overflow-hidden`}>
        <div
          className={`h-full rounded-full ${barColor} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

/* ============================================
   Format Currency
   ============================================ */
const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/* ============================================
   Halaman Utama: MarketingDetail
   ============================================ */
export const MarketingDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showSidebar, setShowSidebar] = useState(false);
  const [marketing, setMarketing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setMarketing(DUMMY_MARKETING);
      setIsLoading(false);
    }, 800);
  }, [id]);

  if (isLoading || !marketing) {
    return (
      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <div className="p-6 flex items-center justify-center h-screen">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-10 h-10 rounded-full border-4 border-slate-600 border-t-ocean-400 animate-spin" />
            <p className="text-slate-400">Memuat data marketing...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const earningsChange = marketing.earnings.bulan_lalu > 0
    ? Math.round(((marketing.earnings.bulan_ini - marketing.earnings.bulan_lalu) / marketing.earnings.bulan_lalu) * 100)
    : 0;

  const psbChange = marketing.psb.bulan_lalu > 0
    ? Math.round(((marketing.psb.bulan_ini - marketing.psb.bulan_lalu) / marketing.psb.bulan_lalu) * 100)
    : 0;

  const psbSelesai = marketing.psb.recent.filter((p) => p.status === "Selesai").length;
  const psbProses = marketing.psb.recent.filter((p) => p.status === "Proses").length;
  const psbGagal = marketing.psb.recent.filter((p) => p.status === "Gagal").length;

  const totalPendapatan = marketing.earnings.rincian.reduce((s, r) => s + r.amount, 0);
  const totalPotongan = marketing.earnings.potongan.reduce((s, p) => s + p.amount, 0);
  const penghasilanBersih = totalPendapatan - totalPotongan;

  return (
    <>
      <Helmet>
        <title>Detail Marketing {marketing.name} - DANELA NET</title>
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
            >
              <FaArrowLeft />
            </button>
            <div className="flex flex-col">
              <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
                Detail Marketing
              </h2>
              <p className="text-xs text-slate-500 hidden md:block mt-0.5">
                Performa dan riwayat PSB
              </p>
            </div>
          </div>
        </Header>

        <main className="p-4 lg:p-6 space-y-6">

          {/* ── Row 1: Profil + Statistik ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Profil */}
            <div className="lg:col-span-1">
              <SectionCard title="Profil Marketing" icon={FaUserCircle}>
                <div className="flex flex-col items-center text-center">
                  {!marketing?.image ? (
                    <div
                      className="w-24 h-24 rounded-2xl flex items-center justify-center font-bold text-3xl flex-shrink-0 mb-4"
                      style={{
                        background: "linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(109, 40, 217, 0.08))",
                        border: "1px solid rgba(139, 92, 246, 0.15)",
                        color: "#A78BFA",
                      }}
                    >
                      {marketing.name?.charAt(0).toUpperCase()}
                    </div>
                  ) : (
                    <img
                      src={`${BaseUrl}/storage/${marketing.image}`}
                      alt={marketing.name}
                      className="w-24 h-24 rounded-2xl object-cover flex-shrink-0 mb-4 border-2 border-slate-700"
                    />
                  )}

                  <h2 className="text-xl font-bold text-gray-100">{marketing.name}</h2>
                  <div className="mt-2 mb-4">
                    <StatusBadge status={marketing?.status?.name} />
                  </div>

                  <div className="w-full space-y-3 text-left mt-2">
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaBriefcase className="text-violet-400 w-5" />
                      <span className="text-slate-200 font-medium">{marketing?.role?.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaEnvelope className="text-violet-400 w-5" />
                      <span className="text-slate-300">{marketing.email}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaPhone className="text-violet-400 w-5" />
                      <span className="text-slate-300">{marketing.phone_number}</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm text-slate-400">
                      <FaMapMarkerAlt className="text-violet-400 w-5 mt-0.5" />
                      <span className="text-slate-300">{marketing.address}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaCalendarAlt className="text-violet-400 w-5" />
                      <span className="text-slate-300">Bergabung: {marketing.join_date}</span>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Statistik */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard
                label="Penghasilan Bulan Ini"
                value={formatCurrency(marketing.earnings.bulan_ini)}
                icon={FaMoneyBillWave}
                color="cyan"
                trend={marketing.earnings.trend}
                trendValue={`${earningsChange > 0 ? "+" : ""}${earningsChange}%`}
              />
              <StatCard
                label="PSB Bulan Ini"
                value={marketing.psb.bulan_ini}
                icon={FaRocket}
                color="emerald"
                trend={marketing.psb.bulan_ini >= marketing.psb.bulan_lalu ? "up" : "down"}
                trendValue={`${psbChange > 0 ? "+" : ""}${psbChange}%`}
                sub={`dari ${marketing.psb.bulan_lalu} bulan lalu`}
              />
              <StatCard
                label="Total PSB"
                value={marketing.psb.total}
                icon={FaBullseye}
                color="violet"
                sub={`Komisi Rp ${formatCurrency(marketing.psb.komisi_per_psb)}/PSB`}
              />
              <StatCard
                label="Ranking"
                value={`#${marketing.psb.ranking}`}
                icon={FaTrophy}
                color="amber"
                sub={`dari ${marketing.psb.total_marketing} marketing`}
              />
            </div>
          </div>

          {/* ── Row 2: Target PSB ── */}
          <SectionCard title="Target PSB Bulan Ini" icon={FaBullseye}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Progress */}
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-400">Pencapaian</span>
                  <span className="text-sm font-bold text-gray-100">
                    {marketing.psb.bulan_ini} / {marketing.psb.target_bulan_ini} PSB
                  </span>
                </div>
                <ProgressBar
                  value={marketing.psb.bulan_ini}
                  max={marketing.psb.target_bulan_ini}
                  color={marketing.psb.bulan_ini >= marketing.psb.target_bulan_ini ? "emerald" : "cyan"}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px] text-slate-500">
                    {Math.round((marketing.psb.bulan_ini / marketing.psb.target_bulan_ini) * 100)}% tercapai
                  </span>
                  {marketing.psb.bulan_ini >= marketing.psb.target_bulan_ini ? (
                    <span className="text-[11px] font-semibold text-emerald-400 flex items-center gap-1">
                      <FaCheckCircle className="text-[10px]" /> Target tercapai
                    </span>
                  ) : (
                    <span className="text-[11px] text-slate-500">
                      Kurang {marketing.psb.target_bulan_ini - marketing.psb.bulan_ini} PSB lagi
                    </span>
                  )}
                </div>
              </div>

              {/* Stat kecil */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                  <p className="text-xl font-bold text-emerald-400">{psbSelesai}</p>
                  <p className="text-[10px] font-semibold text-emerald-500/70 uppercase mt-1">Selesai</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
                  <p className="text-xl font-bold text-amber-400">{psbProses}</p>
                  <p className="text-[10px] font-semibold text-amber-500/70 uppercase mt-1">Proses</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-red-500/5 border border-red-500/10">
                  <p className="text-xl font-bold text-red-400">{psbGagal}</p>
                  <p className="text-[10px] font-semibold text-red-500/70 uppercase mt-1">Gagal</p>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ── Row 3: Penghasilan + PSB Terbaru ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Penghasilan */}
            <SectionCard title="Rincian Penghasilan" icon={FaMoneyBillWave}>
              <div
                className="p-4 rounded-xl mb-5 text-center"
                style={{
                  background: "linear-gradient(135deg, rgba(0, 180, 216, 0.1), rgba(0, 119, 182, 0.05))",
                  border: "1px solid rgba(0, 180, 216, 0.2)",
                }}
              >
                <p className="text-[10px] font-semibold text-ocean-400 uppercase tracking-wider mb-1">Penghasilan Bersih</p>
                <p className="text-3xl font-bold text-gray-100 tracking-tight">{formatCurrency(penghasilanBersih)}</p>
              </div>

              <div className="space-y-4">
                {/* Pendapatan */}
                <div>
                  <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">Pendapatan</p>
                  {marketing.earnings.rincian.map((item) => (
                    <div key={item.id} className="flex items-start justify-between mb-2">
                      <div>
                        <span className="text-sm text-slate-300 font-medium">• {item.label}</span>
                        <p className="text-[11px] text-slate-500 pl-4">{item.desc}</p>
                      </div>
                      <span className="text-sm font-semibold text-emerald-400 whitespace-nowrap ml-4">+ {formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>

                {/* Potongan */}
                {marketing.earnings.potongan.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2">Potongan</p>
                    {marketing.earnings.potongan.map((item) => (
                      <div key={item.id} className="flex justify-between items-center mb-2">
                        <span className="text-sm text-slate-400 pl-2">• {item.label}</span>
                        <span className="text-sm font-semibold text-red-400">- {formatCurrency(item.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Total Tahun */}
                <div className="pt-3 border-t border-slate-700/30">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Total Tahun Ini</span>
                    <span className="text-sm font-bold text-violet-400">{formatCurrency(marketing.earnings.total_tahun_ini)}</span>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* PSB Terbaru */}
            <SectionCard title="PSB Terbaru" icon={FaRocket}>
              <div className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1">
                {marketing.psb.recent.map((psb) => (
                  <div
                    key={psb.id}
                    className="p-3 rounded-xl flex items-center justify-between"
                    style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        psb.status === "Selesai"
                          ? "bg-emerald-500/10 text-emerald-400"
                          : psb.status === "Proses"
                            ? "bg-amber-500/10 text-amber-400"
                            : "bg-red-500/10 text-red-400"
                      }`}>
                        {psb.status === "Selesai" ? <FaCheckCircle className="text-sm" /> : <FaClock className="text-sm" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-100 truncate">{psb.pelanggan}</p>
                        <p className="text-xs text-slate-500 font-mono">{psb.internet_number} · {psb.package}</p>
                        <p className="text-[10px] text-slate-600 mt-0.5">{psb.date}</p>
                      </div>
                    </div>
                    <div className="ml-3 flex flex-col items-end gap-1.5 flex-shrink-0">
                      <StatusBadge status={psb.status} size="xs" />
                      {psb.komisi > 0 && (
                        <span className="text-[10px] font-semibold text-emerald-400">+{formatCurrency(psb.komisi)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>

        </main>
      </DashboardLayout>
    </>
  );
};